<template>
  <div ref="selectTrigger">
    <SelectRoot v-model:open="isOpen" :disabled="disabled">
      <SelectTrigger
        v-bind="$attrs"
        :class="{ 'border-dark-blue-50 bg-dark-blue': type === 'dark-mode', 'bg-white': type === 'primary' }"
        class="inline-flex h-[48px] w-full items-center justify-between gap-[5px] rounded border px-[15px] leading-none text-theme-1 shadow-black/10 outline-none data-[disabled]:pointer-events-none data-[disabled]:bg-theme-placeholder-bg data-[placeholder]:text-theme-non-active"
      >
        <span
          v-if="!!selectedOptions?.length"
          :class="{ 'text-grayish-white': type === 'dark-mode', 'text-black': type === 'primary' }"
        >
          {{ selectedOptionsText }}
        </span>

        <span v-else class="text-gray-400">
          {{ placeholder }}
        </span>

        <img :class="{ 'rotate-180': isOpen, invert: type === 'dark-mode' }" :src="ArrowDown.src" />
      </SelectTrigger>

      <SelectContent
        position="popper"
        class="data-[side=top]:animate-slideDownAndFade data-[side=right]:animate-slideLeftAndFade data-[side=bottom]:animate-slideUpAndFade data-[side=left]:animate-slideRightAndFade z-[100] max-h-40 min-w-[12rem] overflow-y-scroll rounded shadow-[0px_10px_38px_-10px_rgba(22,_23,_24,_0.35),_0px_10px_20px_-15px_rgba(22,_23,_24,_0.2)] will-change-[opacity,transform]"
        :style="{ width: selectMenuWidth }"
        :body-lock="false"
      >
        <div
          class="checkboxes-container"
          :class="{ 'bg-dark-blue': type === 'dark-mode', 'bg-white': type === 'primary' }"
        >
          <IAICheckbox
            v-for="option in options"
            :id="option.key"
            :key="option.key"
            v-model="option.isSelected"
            size="large"
            :type="type === 'primary' ? 'primary' : 'secondary'"
            @update:modelValue="(newValue) => onCheckboxChange(newValue, option)"
          >
            {{ option.label }}
          </IAICheckbox>
        </div>
      </SelectContent>
    </SelectRoot>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, PropType, ref } from "vue";
import { SelectContent, SelectRoot, SelectTrigger } from "radix-vue";

import ArrowDown from "../IAIIcon/icons/ArrowDown.svg";
import { IAICheckbox } from "@/core-ui-vue";

const MAX_ITEMS = 3;
type MultiSelectWrapperType = "primary" | "dark-mode";

export interface MultiOption {
  key: string;
  label: string;
  isSelected: boolean;
}

defineProps({
  placeholder: { type: String, default: "Select" },
  disabled: { type: Boolean, default: false },
  type: {
    type: String as PropType<MultiSelectWrapperType>,
    default: "primary",
  },
  options: {
    type: Array as PropType<MultiOption[]>,
    required: true,
  },
});

const isOpen = ref(false);
const selectTrigger = ref<HTMLElement | null>(null);
const selectMenuWidth = ref();
const selectedOptions = ref<MultiOption[]>([]);

const onCheckboxChange = (newValue: boolean, option: MultiOption) => {
  if (newValue) {
    selectedOptions.value.push(option);
  } else {
    selectedOptions.value = selectedOptions.value.filter((currentOption) => currentOption.key !== option.key);
  }
};

const selectedOptionsText = computed(() => {
  if (!selectedOptions.value) return "";
  if (selectedOptions.value.length === 1) return selectedOptions.value[0].label;

  let slicedLabels = selectedOptions.value
    .map((option) => option.label)
    .slice(0, MAX_ITEMS)
    .join(", ");

  if (selectedOptions.value.length > MAX_ITEMS) {
    slicedLabels += ` + ${selectedOptions.value.length - MAX_ITEMS} more`;
  }
  return slicedLabels;
});

onMounted(() => {
  if (selectTrigger.value) {
    selectMenuWidth.value = `${selectTrigger.value.offsetWidth}px`;
  }
});
</script>

<style scoped lang="scss">
.checkboxes-container {
  padding-top: 0.5rem;
  padding-left: 1rem;
  display: flex;
  flex-direction: column;
}
::v-deep .flex {
  padding: 8px 0;
}
</style>
